
/** Strip non-digits, optionally truncating to `max` digits. */
export function digitsOnly(v: string, max?: number): string {
  const d = v.replace(/\D/g, "");
  return max == null ? d : d.slice(0, max);
}

/** Progressive US phone mask: digits → "(123) 456-7890". */
export function formatPhone(v: string): string {
  const d = digitsOnly(v, 10);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** EIN mask: digits → "XX-XXXXXXX". */
export function formatEin(v: string): string {
  const d = digitsOnly(v, 9);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}-${d.slice(2)}`;
}

/** SSN mask: digits → "XXX-XX-XXXX". */
export function formatSsn(v: string): string {
  const d = digitsOnly(v, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

/** ZIP mask: digits → "12345" or "12345-6789". */
export function formatZip(v: string): string {
  const d = digitsOnly(v, 9);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/** Routing number mask: digits only, max 9. */
export function formatRouting(v: string): string {
  return digitsOnly(v, 9);
}

/** Number of digits in a string, ignoring separators. */
function digitCount(v: string): number {
  return v.replace(/\D/g, "").length;
}

/** SSN: exactly 9 digits (separators allowed). */
export function isSsn9(v: string): boolean {
  return digitCount(v) === 9;
}

/** EIN: exactly 9 digits (separators allowed). */
export function isEin(v: string): boolean {
  return digitCount(v) === 9;
}

/** ITIN: 9 digits, starts with 9 (ITINs always begin with 9). */
export function isItin(v: string): boolean {
  const d = v.replace(/\D/g, "");
  return d.length === 9 && d.startsWith("9");
}

/** Phone/fax: exactly 10 digits (separators allowed). */
export function isPhone10(v: string): boolean {
  return digitCount(v) === 10;
}

/** US ZIP: 5 or 9 digits. */
export function isZip(v: string): boolean {
  const n = digitCount(v);
  return n === 5 || n === 9;
}

/** Bank routing number: exactly 9 digits. */
export function isRouting9(v: string): boolean {
  return digitCount(v) === 9;
}

/** Bank routing number: 9 digits passing the ABA check-digit formula. */
export function isAbaRouting(v: string): boolean {
  const d = v.replace(/\D/g, "");
  if (d.length !== 9) return false;
  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  const sum = weights.reduce((acc, w, i) => acc + w * Number(d[i]), 0);
  return sum > 0 && sum % 10 === 0;
}

/** Hide every digit but the last `keep`, dropping separators: 12-3456789 → *****6789. */
export function maskSecret(v: string, keep = 4): string {
  const digits = digitsOnly(v);
  if (!digits) return v;
  const shown = digits.length <= keep ? "" : digits.slice(digits.length - keep);
  return "*".repeat(digits.length - shown.length) + shown;
}

/** Loose email shape: something@something.tld. */
export function isEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

/** MCC/SIC: exactly 4 digits. */
export function isMcc4(v: string): boolean {
  return /^\d{4}$/.test(v.trim());
}

/** Letters (Unicode, incl. accented), spaces, hyphens and apostrophes only (names). */
export function isLettersOnly(v: string): boolean {
  return /^\p{L}[\p{L}\p{M}\s'-]*$/u.test(v.trim());
}
